import * as React from 'react';
import { useCallback, useImperativeHandle, useState } from 'react';
import { type LayerProps, Map as MapGL, MapRef, NavigationControl, ScaleControl, ViewState } from 'react-map-gl/mapbox';
import { Pointer } from './pointer';
import { Location } from '../../../pages/spotted-lanternfly-vineyard-risk';
import type { FeatureCollection, GeoJsonProperties, Geometry } from 'geojson';


// SLF-specific constants
const SLF_MAP_STYLE = "mapbox://styles/nysipm/cmrdndlpg000601pi7i1i1ycn";
const VECTOR_LAYER_ID = 'risk-vector';

const StyleToggle = ({ currentStyle, onStyleChange }: {
  currentStyle: 'satellite' | 'satellite-hidden',
  onStyleChange: () => void
}) => {
  return (
    <div className="absolute top-2 left-2 bg-white rounded shadow-md z-10">
      <button
        className={`px-3 py-1 text-sm rounded-l border ${
          currentStyle === 'satellite' ? 'bg-blue-500 text-white border-blue-500' : 'bg-white text-gray-700 border-gray-300'
        }`}
        onClick={onStyleChange}
      >
        Satellite
      </button>
    </div>
  );
};

// Calculate risk from vector layer features. risk_level is an integer 0–100
// index, returned as-is (no scaling, no formatting).
const calculateRiskFromVector = (map: any, point: any): number | null => {
  try {
    const features = map.queryRenderedFeatures(point, {
      layers: [VECTOR_LAYER_ID]
    });

    if (features && features.length > 0) {
      const riskValue = features[0].properties.risk_level;

      if (riskValue !== undefined) {
        console.log('Risk in Vector: ' + riskValue);
        return riskValue;
      }
    }

    return 0;
  } catch (error) {
    console.error('Error querying vector features:', error);
    return null;
  }
};

// Dynamically create hatch pattern SVG for a given color and angle
const createHatchPattern = (color: string, patternId: string, angle: number): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const svg = `
        <svg width="32" height="32" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="${patternId}" patternTransform="rotate(${angle})" patternUnits="userSpaceOnUse" width="32" height="32">
              <path d="M-16,16 l32,-32 M0,32 l32,-32 M16,48 l32,-32"
                    stroke="${color}"
                    stroke-width="1"
                    fill="none"
                    stroke-linecap="round"/>
            </pattern>
          </defs>
          <rect width="32" height="32" fill="url(#${patternId})"/>
        </svg>
      `;

    const img = new Image(8, 8);
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg);
  });
};


export interface SLFMapHandle {
  getMap: () => any;
  queryRiskAtLocation: (lat: number, lng: number) => void;
}

interface SLFMapProps {
  customHeight?: number | string;
  onLocationSelect?: (lat: number, lng: number) => void;
  location: Location;
  onRiskCalculated?: (risk: number | null) => void;
  onMapReady?: () => void;
  initialViewState: Partial<ViewState>;
  onStationClick?: (stationId: string) => void;
  geoJSON: FeatureCollection<Geometry, GeoJsonProperties>;
  layerStyle: LayerProps;
}

const SLFMap = React.forwardRef<SLFMapHandle, SLFMapProps>(({
                                                              customHeight = "400px",
                                                              onLocationSelect,
                                                              location,
                                                              onRiskCalculated,
                                                              onMapReady,
                                                              initialViewState,
                                                              onStationClick,
                                                              geoJSON,
                                                              layerStyle,
                                                            }, ref) => {
  const mapRef = React.useRef<MapRef>();
  const [cursor, setCursor] = useState('auto');
  const [showSatellite, setShowSatellite] = useState(false);
  const [animateMarker, setAnimateMarker] = useState(false);

  // Expose map methods to parent via ref
  useImperativeHandle(ref, () => ({
    getMap: () => {
      return mapRef.current?.getMap();
    },
    queryRiskAtLocation: (lat: number, lng: number) => {
      const map = mapRef.current?.getMap();
      if (!map || !map.isStyleLoaded()) return;

      const point = map.project([lng, lat]);
      const risk = calculateRiskFromVector(map, point);

      if (onRiskCalculated) {
        onRiskCalculated(risk);
      }
    }
  }), [onRiskCalculated]);

  // Initialize SLF-specific layers on map load
  const handleOnLoad = useCallback(() => {
    const executeSetup = async () => {
      const map = mapRef.current?.getMap();
      if (!map || !map.isStyleLoaded()) return;

      // Risk ramp resampled from the old 0–4 scale onto the 10 deciles, so this
      // matches the familiar tool: the old ramp collapsed 3.0/3.5/4.0 to one
      // yellow, so here levels 8–10 (80/90/100) share yellow and yellow appears
      // from ~80% rather than only at 100. Level 0 is transparent (the NA-adjacent
      // 6–9 range; anything <6.25 was dropped in R). Keep in sync with RISK_STOPS
      // in the page (index.tsx).
      const riskColors = [
        { level: 0, bg: 'rgba(68,1,84, 0)', hatch: 'rgba(68,1,84, 0)' },
        { level: 1, bg: 'rgba(61,66,128, .7)', hatch: 'rgba(61,66,128, 0.9)' },
        { level: 2, bg: 'rgba(53,95,141, .7)', hatch: 'rgba(53,95,141, 0.9)' },
        { level: 3, bg: 'rgba(46,110,142, .7)', hatch: 'rgba(46,110,142, 0.9)' },
        { level: 4, bg: 'rgba(44,133,138, .7)', hatch: 'rgba(44,133,138, 0.9)' },
        { level: 5, bg: 'rgba(53,183,121, .7)', hatch: 'rgba(53,183,121, 0.9)' },
        { level: 6, bg: 'rgba(126,209,78, .7)', hatch: 'rgba(126,209,78, 0.9)' },
        { level: 7, bg: 'rgba(209,225,49, .7)', hatch: 'rgba(209,225,49, 0.9)' },
        { level: 8, bg: 'rgba(253,231,37, .7)', hatch: 'rgba(253,231,37, 0.9)' },
        { level: 9, bg: 'rgba(253,231,37, .7)', hatch: 'rgba(253,231,37, 0.9)' },
        { level: 10, bg: 'rgba(253,231,37, .7)', hatch: 'rgba(253,231,37, 0.9)' },
      ];

      // Add a hatch pattern image for each decile. Angles are spread across
      // 180° (180/11 ≈ 16.36° apart) so all 11 orientations are distinct.
      // Level 0 gets a transparent pattern so the 6–9 range shows no hatch
      // while still giving the fill-pattern step expression a valid default.
      for (const { level } of riskColors) {
        try {
          const patternId = `hatch-pattern-${level}`;
          const patternColor = level === 0 ? 'rgba(0,0,0,0)' : 'black';
          const img = await createHatchPattern(patternColor, patternId, level * (180 / 11));
          if (!map.hasImage(patternId)) {
            map.addImage(patternId, img);
          }
        } catch (error) {
          console.error(`Error creating hatch pattern for level ${level}:`, error);
        }
      }

      // Add vector layer with background fill
      map.addLayer({
        id: VECTOR_LAYER_ID,
        type: 'fill',
        source: {
          type: 'vector',
          url: 'mapbox://nysipm.ccv3hhsw8gf7'
        },
        'source-layer': 'debd2aa6228f63230cf2',
        'slot': 'middle',
        paint: {
          'fill-color': [
            'step', ['get', 'risk_level'],
            riskColors[0].bg,        // covers 6–9 (below first threshold)
            10, riskColors[1].bg,    // 10–19
            20, riskColors[2].bg,    // 20–29
            30, riskColors[3].bg,    // 30–39
            40, riskColors[4].bg,    // 40–49
            50, riskColors[5].bg,    // 50–59
            60, riskColors[6].bg,    // 60–69
            70, riskColors[7].bg,    // 70–79
            80, riskColors[8].bg,    // 80–89
            90, riskColors[9].bg,    // 90–99
            100, riskColors[10].bg,  // exactly 100
          ],
          'fill-opacity': 1,
        },
      });

      // Add hatch pattern layer on top, keyed to the same decile boundaries
      // as the fill. The default (below 10) is the transparent level-0 pattern.
      map.addLayer({
        id: `${VECTOR_LAYER_ID}-hatch`,
        type: 'fill',
        source: {
          type: 'vector',
          url: 'mapbox://nysipm.ccv3hhsw8gf7'
        },
        'source-layer': 'debd2aa6228f63230cf2',
        'slot': 'middle',
        minzoom: 8,
        paint: {
          'fill-pattern': [
            'step', ['get', 'risk_level'],
            'hatch-pattern-0',
            10, 'hatch-pattern-1',
            20, 'hatch-pattern-2',
            30, 'hatch-pattern-3',
            40, 'hatch-pattern-4',
            50, 'hatch-pattern-5',
            60, 'hatch-pattern-6',
            70, 'hatch-pattern-7',
            80, 'hatch-pattern-8',
            90, 'hatch-pattern-9',
            100, 'hatch-pattern-10',
          ],
          'fill-opacity': .5,
        }
      });

      // Add NEWA stations source and layer imperatively
      if (!map.getSource('newa')) {
        map.addSource('newa', {
          type: 'geojson',
          data: geoJSON,
          minzoom: 6,
        });
      }

      if (!map.getLayer('newa')) {
        map.addLayer({
          id: layerStyle.id as string,
          type: layerStyle.type as 'symbol',
          source: 'newa',
          layout: layerStyle.layout,
          paint: layerStyle.paint,
          ...(layerStyle.minzoom !== undefined && { minzoom: layerStyle.minzoom }),
          ...(layerStyle.maxzoom !== undefined && { maxzoom: layerStyle.maxzoom }),
        } as any);
      }

      // Notify parent that map is ready after everything is set up
      const handleInitialIdle = () => {
        if (map.isStyleLoaded()) {
          if (onMapReady) {
            onMapReady();
          }
          map.off('idle', handleInitialIdle);
        }
      };

      // If already idle and loaded, notify immediately
      if (map.isStyleLoaded() && !map.isMoving()) {
        if (onMapReady) {
          onMapReady();
        }
      } else {
        // Otherwise wait for idle event
        map.on('idle', handleInitialIdle);
      }
    };

    // Wait for style to load
    if (mapRef.current.isStyleLoaded()) {
      void executeSetup();
    } else {
      mapRef.current.once('styledata', () => void executeSetup());
    }
  }, [geoJSON, layerStyle, onMapReady]);

  // Handle map clicks - check for station clicks first, then notify location change
  const handleMapClick = useCallback((event: any) => {
    const map = event.target;
    if (!map || !map.isStyleLoaded()) {
      console.warn('Map not ready for feature query');
      return;
    }

    // First, check if a station was clicked
    if (onStationClick) {
      const stationFeatures = map.queryRenderedFeatures(event.point, {
        layers: ['newa']
      });

      if (stationFeatures && stationFeatures.length > 0) {
        const stationId = stationFeatures[0].properties.id;
        if (stationId) {
          console.log('Station clicked:', stationId);
          onStationClick(stationId);
          return; // Don't process as regular location click
        }
      }
    }

    // If no station was clicked, proceed with regular location selection
    const { lng, lat } = event.lngLat;

    // Trigger marker animation
    setAnimateMarker(true);
    setTimeout(() => setAnimateMarker(false), 500);

    if (onLocationSelect) {
      onLocationSelect(lat, lng);
    }
  }, [onLocationSelect, onStationClick]);

  // Toggle satellite layer
  const toggleSatellite = useCallback(() => {
    const map = mapRef.current?.getMap();
    if (!map || !map.isStyleLoaded()) return;

    const newShowSatellite = !showSatellite;

    try {
      if (map.getLayer('mapbox-satellite')) {
        map.setPaintProperty(
          'mapbox-satellite',
          'raster-opacity',
          newShowSatellite ? 1 : 0
        );
        map.setLayoutProperty(
          'mapbox-satellite',
          'visibility',
          newShowSatellite ? 'visible' : 'none'
        );
        setShowSatellite(newShowSatellite);
      }
    } catch (error) {
      console.error('Error toggling satellite layer:', error);
    }
  }, [showSatellite]);

  const onMouseEnter = useCallback(() => setCursor('pointer'), []);
  const onMouseLeave = useCallback(() => setCursor('auto'), []);

  return (
    <div className="relative border border-gray-300 rounded-md" style={{ height: customHeight }}>
      {/* Satellite toggle */}
      <StyleToggle
        currentStyle={showSatellite ? 'satellite' : 'satellite-hidden'}
        onStyleChange={toggleSatellite}
      />

      <MapGL
        id="slfRiskMap"
        ref={mapRef}
        initialViewState={initialViewState}
        style={{ width: '100%', height: '100%' }}
        mapStyle={SLF_MAP_STYLE}
        mapboxAccessToken={process.env.GATSBY_MAPBOX_TOKEN}
        clickTolerance={3}
        dragRotate={false}
        onClick={handleMapClick}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        cursor={cursor}
        onLoad={handleOnLoad}
      >
        <Pointer lat={location.latitude} lng={location.longitude} animate={animateMarker}/>

        <NavigationControl
          position="top-right"
          showCompass={false}
        />

        <ScaleControl unit="imperial" position="bottom-right"/>
      </MapGL>
    </div>
  );
});

SLFMap.displayName = 'SLFMap';

export default SLFMap;